import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type {
  CreateRecordingPayload,
  RecordedClass,
  RecordingDetail,
  RecordingLibrary,
  UpdateRecordingPayload,
  VideoUploadTarget,
  VideoUploadTargetPayload,
} from '@/types/recording';

/**
 * Every call the recorded-class screens make.
 *
 * Its own file beside `homework.api.ts`, for the same reason: a feature's
 * network layer that grows without bound is the one nobody can find anything in
 * (CLAUDE.md §6).
 */
export const recordingsApi = {
  /** A section's library, newest first, with the figures the tiles show. */
  list: (classSectionUuid: string, filters?: { onDate?: string; topic?: string }) =>
    unwrap(
      apiClient.get<ApiSuccess<RecordingLibrary>>('/institute/recorded-classes', {
        params: {
          classSectionUuid,
          ...(filters?.onDate && { onDate: filters.onDate }),
          ...(filters?.topic && { topic: filters.topic }),
        },
      }),
    ),

  /**
   * One lesson with the *whole roster* against it — the children who have not
   * watched are the ones a teacher opens the page to find.
   */
  detail: (uuid: string) =>
    unwrap(apiClient.get<ApiSuccess<RecordingDetail>>(`/institute/recorded-classes/${uuid}`)),

  /**
   * Asks for somewhere to put a video, before the lesson row exists.
   *
   * Two steps rather than one multipart post, because a lesson recording is
   * measured in hundreds of megabytes: sending it through this API would hold
   * the whole file in the server's memory and time out long before it arrived.
   * The bytes go straight to storage instead, and the key comes back here.
   */
  createUploadTarget: (payload: VideoUploadTargetPayload) =>
    unwrap(
      apiClient.post<ApiSuccess<VideoUploadTarget>>(
        '/institute/recorded-classes/upload-target',
        payload,
      ),
    ),

  create: (payload: CreateRecordingPayload) =>
    unwrap(apiClient.post<ApiSuccess<RecordedClass>>('/institute/recorded-classes', payload)),

  /**
   * Corrects a lesson's title, notes or video. A replacement upload goes to
   * storage through `createUploadTarget` first, exactly as a first one does.
   */
  update: (uuid: string, payload: UpdateRecordingPayload) =>
    unwrap(
      apiClient.patch<ApiSuccess<RecordedClass>>(`/institute/recorded-classes/${uuid}`, payload),
    ),

  /** A soft delete: who watched it is a record of the term. */
  remove: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/recorded-classes/${uuid}`)),
};

/**
 * Sends the file to storage against a target the server signed.
 *
 * Deliberately **not** on `apiClient`. That instance carries our base URL, our
 * cookies and our auth header, and this request goes to a third-party bucket —
 * sending a session credential to another origin is how one leaks. Plain
 * `XMLHttpRequest`, no `withCredentials`, only the headers that were signed.
 *
 * `XMLHttpRequest` rather than `fetch` for one reason: upload progress. `fetch`
 * still cannot report how much of a request body has gone, and a teacher
 * sending 400 MB with no feedback assumes it has hung and closes the tab.
 */
export function sendVideoToStorage(
  target: VideoUploadTarget,
  file: File,
  options: { onProgress?: (percent: number) => void; signal?: AbortSignal } = {},
): Promise<void> {
  const { onProgress, signal } = options;

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Upload cancelled', 'AbortError'));
      return;
    }

    const request = new XMLHttpRequest();
    request.open(target.method, target.url, true);
    for (const [header, value] of Object.entries(target.headers)) {
      request.setRequestHeader(header, value);
    }

    request.upload.addEventListener('progress', (event) => {
      // `lengthComputable` is false while the browser is still working the
      // total out; reporting 0% then would make the bar jump backwards.
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    const abort = () => request.abort();
    signal?.addEventListener('abort', abort, { once: true });

    const settle = (finish: () => void) => {
      signal?.removeEventListener('abort', abort);
      finish();
    };

    request.addEventListener('load', () =>
      settle(() =>
        // The bucket answers 200 on a successful PUT. Its error bodies are XML
        // naming our bucket and key, so they are never shown to the teacher.
        request.status >= 200 && request.status < 300
          ? resolve()
          : reject(new Error('The video could not be sent. Check your connection and try again.')),
      ),
    );

    request.addEventListener('error', () =>
      settle(() =>
        reject(new Error('The video could not be sent. Check your connection and try again.')),
      ),
    );

    request.addEventListener('abort', () =>
      settle(() => reject(new DOMException('Upload cancelled', 'AbortError'))),
    );

    request.send(file);
  });
}
